import type { Runner } from '@ps/isp-web-services-types';
import { RunnerResultType } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import { unzip } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketTemplateComponent,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import { TXTWCL_Over, TXTWCL_Show_More, TXTWCL_Under } from './AlternateTotal.messages.json';
import styles from './AlternateTotal.scss';

const SHOW_MORE_THRESHOLD = 10;

type OverUnderResultType = RunnerResultType.OVER | RunnerResultType.UNDER;

const isOverUnderResultType = (value: RunnerResultType): value is OverUnderResultType =>
  [RunnerResultType.OVER, RunnerResultType.UNDER].includes(value);

const AlternateTotal: MarketTemplateComponent<SingleMarketTemplateProps> = ({ marketId }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId));

  const overUnderRunners = market?.runners.reduce<{
    groups: Record<OverUnderResultType, (Runner & { runnerNameValue: number })[]>;
    allRunnerNameValues: number[];
  }>(
    (runners, runner) => {
      if (runner.resultType && isOverUnderResultType(runner.resultType)) {
        const validRunnerMatch = runner.runnerName.match(/^(.+?) \(([\d.]+)\)$/);

        if (!validRunnerMatch) return runners;

        const overUnderGroup = runner.resultType;

        runners.groups[overUnderGroup].push({
          ...runner,
          runnerNameValue: parseFloat(validRunnerMatch[2])
        });

        runners.allRunnerNameValues.push(parseFloat(validRunnerMatch[2]));

        return runners;
      }

      return runners;
    },
    {
      groups: { [RunnerResultType.OVER]: [], [RunnerResultType.UNDER]: [] },
      allRunnerNameValues: []
    }
  );

  const uniqueRunnerNameValues = Array.from(new Set(overUnderRunners?.allRunnerNameValues)).sort(
    (a, b) => a - b
  );

  const results: Record<string, ((Runner & { runnerNameValue: number }) | null)[]> = {};

  Object.entries(overUnderRunners?.groups ?? {}).forEach(([key, value]) => {
    results[key] = uniqueRunnerNameValues.map(
      (uniqueRunnerNameValue) =>
        value.find((runner) => runner.runnerNameValue === uniqueRunnerNameValue) ?? null
    );
  });

  const overRunners = results[RunnerResultType.OVER];
  const underRunners = results[RunnerResultType.UNDER];

  const rows = useMemo(() => unzip([overRunners, underRunners]), [overRunners, underRunners]);

  const [rowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    rows,
    marketId,
    SHOW_MORE_THRESHOLD
  );

  if (!market) return null;

  return (
    <>
      <table data-testid="market-template-alternate-total" className={styles.table}>
        <thead>
          <tr>
            <th scope="col">
              <Translation {...TXTWCL_Over} />
            </th>
            <th scope="col">
              <Translation {...TXTWCL_Under} />
            </th>
          </tr>
        </thead>
        <tbody>
          {rowsToDisplay.map((row, i) => (
            <tr key={`row-${market.marketId}-${i}`}>
              {row.map((runner, j) => {
                if (!runner) {
                  return (
                    <td key={`cell-${market.marketId}-${i}-${j}`}>
                      <span className={styles.emptySelection}>-</span>
                    </td>
                  );
                }

                return (
                  <td key={runner.selectionId}>
                    {
                      <Selection
                        competitionId={market.competitionId}
                        eventId={market.eventId}
                        eventTypeId={market.eventTypeId}
                        handicap={runner.handicap}
                        legTypes={market.legTypes}
                        marketId={market.marketId}
                        marketStatus={market.marketStatus}
                        marketType={market.marketType}
                        odds={runner.winRunnerOdds}
                        runnerResultType={runner.resultType}
                        runnerStatus={runner.runnerStatus}
                        selectionId={runner.selectionId}
                        selectionText={`${
                          runner.resultType === RunnerResultType.OVER ? '>' : '<'
                        } ${String(runner.runnerNameValue)}`}
                        startTime={market.marketTime}
                        isMultiline
                      />
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

AlternateTotal.marketTemplateType = 'EVENT';

export default AlternateTotal;
